import { deepBlueSky } from "@/styles/theme";
import { Avatar, Box, Container, Grid, HStack, Text } from "@chakra-ui/react";
import blackBird from "@root/public/images/icons/clarifayo_fundoAzul2.svg";
import { Carousel } from "antd";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";

const TestimonialCard = ({
  avatar,
  testimonial,
  name,
  jobTitle,
}: {
  avatar: string;
  testimonial: string;
  name: string;
  jobTitle: string;
}) => {
  return (
    <Box p={6} w="full" fontFamily={"Gotham"}>
      <HStack gap={4} mb={8}>
        <Avatar
          size={{ base: "lg", md: "xl" }}
          src={avatar}
          name={name}
          boxShadow={"sm"}
        />
        <Box>
          <Text fontSize="1.5rem" color="white" fontWeight={600}>
            {name}
          </Text>
          <Text fontSize="sm" color="white" fontWeight={400} noOfLines={2}>
            {jobTitle}
          </Text>
        </Box>
      </HStack>
      <Text
        mb={4}
        fontSize={{
          base: "xl",
          md: "2xl",
        }}
        maxW="full"
        color="white"
        noOfLines={11}
      >
        <Text as="span" color="brand.900">
          &quot;
        </Text>
        <Text as="span" dangerouslySetInnerHTML={{ __html: testimonial }} />
        <Text as="span" color="brand.900">
          &quot;
        </Text>
      </Text>
    </Box>
  );
};

export const TestimonialsSection = () => {
  const t = useTranslations();
  return (
    <Grid
      bgColor={deepBlueSky}
      color="white"
      w="full"
      id="testimonials"
      maxW="full"
      overflow="hidden"
      gridTemplateColumns={{
        base: "1fr",
        md: "6fr 4fr",
      }}
    >
      <Container maxW={"container.xl"}>
        <Box
          w="full"
          py={{ base: 8, md: 8 }}
          px={{
            base: 4,
            md: 16,
          }}
          maxW={{
            base: "100vw",
            md: "60vw",
          }}
        >
          <Box w="full" h="full" position="relative">
            <Carousel
              arrows
              infinite
              autoplay
              className="testimonial-carousel"
              dots={false}
              autoplaySpeed={5000}
              nextArrow={
                <Box
                  fontSize={40}
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <ChevronRightIcon />
                </Box>
              }
              prevArrow={
                <Box
                  fontSize={40}
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <ChevronLeftIcon />
                </Box>
              }
            >
              <Box>
                <TestimonialCard
                  avatar="/images/avatars/avatar-1.jpg"
                  testimonial={`${t("Testimonials.testimonial1")}`}
                  name="Marta Roquette"
                  jobTitle={`${t("Testimonials.jobTitle1")}`}
                />
              </Box>
              <Box>
                <TestimonialCard
                  avatar="/images/avatars/avatar-2.png"
                  testimonial={`${t("Testimonials.testimonial2")}`}
                  name="Susana Rodrigues"
                  jobTitle={`${t("Testimonials.jobTitle2")}`}
                />
              </Box>
              <Box>
                <TestimonialCard
                  avatar="/images/avatars/avatar-3.png"
                  testimonial={`${t("Testimonials.testimonial3")}`}
                  name="Teresa Horta e Costa"
                  jobTitle={`${t("Testimonials.jobTitle3")}`}
                />
              </Box>
              <Box>
                <TestimonialCard
                  avatar="/images/avatars/avatar-4.jpg"
                  testimonial={`${t("Testimonials.testimonial4")}`}
                  name="Inês Pinto Coelho"
                  jobTitle={`${t("Testimonials.jobTitle4")}`}
                />
              </Box>
              <Box>
                <TestimonialCard
                  avatar="/images/avatars/avatar-5.jpg"
                  testimonial={`${t("Testimonials.testimonial5")}`}
                  name="Maria Seruya"
                  jobTitle={`${t("Testimonials.jobTitle5")}`}
                />
              </Box>
            </Carousel>
          </Box>
        </Box>
      </Container>
      <Box w="full" h="full" position="relative" overflow="hidden">
        <Box
          position="absolute"
          top={"80px"}
          right={"-16%"}
          w="full"
          h={{
            base: "400px",
            md: "full",
          }}
        >
          <Image
            src={blackBird}
            width={800}
            height={800}
            alt="Black Bird"
            style={{
              width: "150%",
              height: "150%",
              objectFit: "fill",
              zIndex: 1,
            }}
          />
        </Box>
      </Box>
    </Grid>
  );
};
